import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Grid,
  Card,
  CardContent,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { accountsReceivableApi } from '../../api/accountsReceivable';
import { StatusBadge } from '../../components/common/StatusBadge';
import { useToast } from '../../hooks/useToast';
import { EmptyState } from '../../components/common/EmptyState';

export const AccountsReceivable = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [branchFilter, setBranchFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const { showToast, ToastComponent } = useToast();

  const { data: accounts, isLoading, error } = useQuery({
    queryKey: ['accountsReceivable'],
    queryFn: () => accountsReceivableApi.getAccountsReceivable(),
  });

  // Handle errors
  useEffect(() => {
    if (error) {
      showToast(error.message || 'Error al cargar cuentas por cobrar', 'error');
    }
  }, [error, showToast]);

  // Extract unique branches for filter
  const branches = useMemo(() => {
    if (!accounts) return [];
    const uniqueBranches = [...new Set(accounts.map(acc => acc.sucursal_nombre).filter(Boolean))];
    return uniqueBranches.sort();
  }, [accounts]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (!accounts) return { total: 0, totalAmount: 0 };
    const total = accounts.length;
    const totalAmount = accounts.reduce((sum, acc) => sum + (parseFloat(acc.monto_aprobado) || 0), 0);
    return { total, totalAmount };
  }, [accounts]);

  // Filter and search accounts
  const filteredAccounts = useMemo(() => {
    if (!accounts) return [];

    return accounts.filter(account => {
      // Search filter
      const matchesSearch = !searchTerm || 
        (account.cliente_nombre && account.cliente_nombre.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (account.loan_id && account.loan_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (account.cliente_identificacion && account.cliente_identificacion.includes(searchTerm));

      // Branch filter
      const matchesBranch = branchFilter === 'all' || account.sucursal_nombre === branchFilter;

      // Status filter
      const matchesStatus = statusFilter === 'all' || account.estado_prestamo === statusFilter;

      return matchesSearch && matchesBranch && matchesStatus;
    });
  }, [accounts, searchTerm, branchFilter, statusFilter]);

  // Calculate pending balance (simplified - monto aprobado menos pagos)
  const getPendingBalance = (account) => {
    // This is a simplified calculation - in a real scenario, you'd need to get
    // the actual paid amount from the backend
    const montoAprobado = parseFloat(account.monto_aprobado) || 0;
    // For now, we'll show the full amount as pending (backend should provide this)
    return montoAprobado;
  };

  // DataGrid columns
  const columns = [
    {
      field: 'loan_id',
      headerName: 'Loan ID',
      width: 150,
      flex: 0.8,
    },
    {
      field: 'cliente_nombre',
      headerName: 'Cliente',
      width: 200,
      flex: 1.2,
    },
    {
      field: 'cliente_identificacion',
      headerName: 'Identificación',
      width: 130,
      flex: 0.8,
    },
    {
      field: 'saldo_pendiente',
      headerName: 'Saldo Pendiente',
      width: 150,
      flex: 1,
      type: 'number',
      valueGetter: (params) => {
        if (!params || !params.row) return 0;
        return getPendingBalance(params.row);
      },
      valueFormatter: (params) => {
        if (!params || params.value === null || params.value === undefined) return '$0.00';
        return new Intl.NumberFormat('es-ES', {
          style: 'currency',
          currency: 'USD',
        }).format(params.value);
      },
      renderCell: (params) => {
        if (!params) return null;
        const value = params.value !== null && params.value !== undefined ? params.value : 0;
        return (
          <Typography
            variant="body2"
            sx={{
              color: value > 0 ? 'warning.main' : 'success.main',
              fontWeight: 500,
            }}
          >
            {new Intl.NumberFormat('es-ES', {
              style: 'currency',
              currency: 'USD',
            }).format(value)}
          </Typography>
        );
      },
    },
    {
      field: 'estado_prestamo',
      headerName: 'Estado',
      width: 120,
      flex: 0.8,
      renderCell: (params) => {
        if (!params) return null;
        return <StatusBadge status={params.value || 'activo'} />;
      },
    },
    {
      field: 'sucursal_nombre',
      headerName: 'Sucursal',
      width: 150,
      flex: 1,
    },
  ];

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Cuentas por Cobrar</Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountBalanceIcon color="primary" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Cuentas
                  </Typography>
                  <Typography variant="h5">
                    {summaryStats.total}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountBalanceIcon color="warning" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Monto Total
                  </Typography>
                  <Typography variant="h5">
                    {new Intl.NumberFormat('es-ES', {
                      style: 'currency',
                      currency: 'USD',
                    }).format(summaryStats.totalAmount)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Buscar por cliente, loan ID o identificación..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Sucursal</InputLabel>
                <Select
                  value={branchFilter}
                  onChange={(e) => setBranchFilter(e.target.value)}
                  label="Sucursal"
                >
                  <MenuItem value="all">Todas</MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch} value={branch}>
                      {branch}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Estado"
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="activo">Activo</MenuItem>
                  <MenuItem value="vencido">Vencido</MenuItem>
                  <MenuItem value="saldado">Saldado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {filteredAccounts.length > 0 && (
              <Grid item xs={12} md={2}>
                <Chip
                  label={`${filteredAccounts.length} resultado(s)`}
                  color="primary"
                  variant="outlined"
                />
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* DataGrid */}
      {isLoading ? (
        <Box sx={{ width: '100%', height: 400 }}>
          <Typography>Cargando...</Typography>
        </Box>
      ) : !filteredAccounts || filteredAccounts.length === 0 ? (
        <EmptyState
          title="Sin Cuentas por Cobrar"
          description={
            accounts && accounts.length > 0
              ? 'No se encontraron cuentas que coincidan con los filtros seleccionados'
              : 'No hay cuentas por cobrar disponibles'
          }
          icon={AccountBalanceIcon}
        />
      ) : (
        <Card>
          <Box sx={{ width: '100%', height: 600 }}>
            <DataGrid
              rows={filteredAccounts.map((account, index) => ({
                id: account.prestamo_original_id || account.id || index,
                ...account,
              }))}
              columns={columns}
              pageSize={25}
              rowsPerPageOptions={[10, 25, 50, 100]}
              disableSelectionOnClick
              sx={{
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
              }}
            />
          </Box>
        </Card>
      )}
      {ToastComponent()}
    </Box>
  );
};

